/**
 * RET-2452 (KTD-4): the payment machine of the past-due zone, pure. It decides
 * what follows each answer of `pay-pending-debts` with `contract: 2` and each
 * answer of Stripe's bank authentication: another POST (same `attempt_id`),
 * an authentication, or the end of the attempt and what the zone shows. The
 * hook runs the steps; nothing here talks to Stripe, the network or React.
 *
 * One attempt = one `attempt_id`, one card, one amount shown. leadbilling's
 * idempotency key is `{attempt_id}:{invoice_id}`, so a repeated POST of the
 * same attempt can never charge an invoice twice, and a new card always gets
 * a new attempt.
 */
import type {
  tPastDueDecline,
  tPastDuePaymentPhase,
} from './components/SubscriptionOverview/PastDuePaymentZone.types';
import {
  formatDebtAmount,
  parseSummaryDate,
  toPastDueDeclineGroup,
  toRetryLimit,
  type tPastDueLocalState,
} from './PastDuePayment.functions';
import type {
  tPaymentMethod,
  tPayPendingDebtsContract2Outcome,
  tPayPendingDebtsRequest,
  tPreviousPaymentMethods,
} from './SubscriptionManager.types';

/** 409 `payment_in_progress`: the same POST again after this wait… */
export const PAST_DUE_IN_PROGRESS_RETRY_MS = 2000;
/** …at most this many times. Then it is our error. */
export const PAST_DUE_IN_PROGRESS_MAX_RETRIES = 3;
/**
 * 3DS watchdog (REL-1, as in `UnlockTrialCapModal`): `stripe.confirmPayment`
 * can hang if the bank's challenge never resolves (closed iframe, blocked
 * popup). The promise cannot be cancelled and may still charge, so past this
 * wait the attempt does NOT end: it stays in `confirming` with its lock, keeps
 * awaiting the same promise and re-reads the summary on the settling cadence
 * until the payment reconciles (paid, the invoice authenticated, or Stripe's
 * own error). Only Stripe's error or a cancel is `authentication_incomplete`
 * with a retry.
 */
export const PAST_DUE_AUTHENTICATION_TIMEOUT_MS = 90_000;
/** `settling`: the summary is read again after this wait… */
export const PAST_DUE_SETTLING_INTERVAL_MS = 3000;
/** …at most this many times. Then the zone stays in `settling`. */
export const PAST_DUE_SETTLING_MAX_READS = 10;

export type tPastDueAttempt = {
  attemptId: string;
  paymentMethodId: string;
  cardBrand: string | null;
  cardLast4: string | null;
  /** Lowercase, as the POST sends it. */
  currency: string;
  /** As the summary has it, for the formatted amounts. */
  displayCurrency: string | null;
  /** «Lo que debes» when the attempt started, in cents. The paid toast uses it. */
  amountShown: number;
  /** The POST to send (again, on a 409). */
  request: tPayPendingDebtsRequest;
  /** What this attempt charged so far, in cents: every POST's `amount_paid` plus each authenticated invoice. */
  charged: number;
  inProgressRetries: number;
  /** Set while Stripe authenticates an invoice. */
  authentication: {
    invoiceAmount: number;
    /** What is owed with the invoice under authentication still unpaid. */
    remaining: number;
    next: { amountRemaining: number; invoiceIds: string[] } | null;
  } | null;
};

export type tPastDuePostAnswer =
  | { ok: true; body: unknown }
  | { ok: false; body: unknown };

export type tPastDueAttemptOutcome =
  | { kind: 'paid'; charged: number }
  | { kind: 'declined'; decline: tPastDueDecline }
  | {
      kind: 'partial';
      decline: tPastDueDecline;
      charged: number;
      remaining: number;
    }
  /** `charged`: what this attempt took before leadbilling noticed (0 when nothing). */
  | { kind: 'debtChanged'; charged: number }
  | { kind: 'attemptReused' }
  | { kind: 'paymentMethodNotFound' }
  | { kind: 'forbidden' }
  | { kind: 'ourError' };

export type tPastDueStep =
  | { kind: 'post'; attempt: tPastDueAttempt; delayMs: number }
  | { kind: 'authenticate'; attempt: tPastDueAttempt; clientSecret: string }
  | { kind: 'finish'; attempt: tPastDueAttempt; outcome: tPastDueAttemptOutcome };

/**
 * What the hook does after the attempt ends:
 * - `confirmPaid`: read the summary; out of past due → toast, else `settling`.
 * - `rereadDebt`: read the summary; the zone shows the new debt, or our error if the read fails.
 * - `rereadSummary` / `rereadCards`: refresh, then the next «Pagar» is a new attempt
 *   (after our own error the button stays off until a reload).
 */
export type tPastDueFollowUp =
  'confirmPaid' | 'rereadDebt' | 'rereadSummary' | 'rereadCards';

export function startPastDueAttempt(options: {
  attemptId: string;
  card: tPaymentMethod;
  /** The summary's `amountRemaining`, in cents: what the screen showed. */
  amountRemaining: number;
  currency: string;
  /** `debtInvoices[].invoiceId`: the invoices of that amount. */
  invoiceIds: string[];
  previousPaymentMethods: tPreviousPaymentMethods;
}): tPastDueAttempt {
  const currency = options.currency.toLowerCase();
  return {
    attemptId: options.attemptId,
    paymentMethodId: options.card.paymentMethodId,
    cardBrand: options.card.brand ?? null,
    cardLast4: options.card.lastDigits ?? null,
    currency,
    displayCurrency: options.currency || null,
    amountShown: options.amountRemaining,
    request: {
      payment_method_id: options.card.paymentMethodId,
      previous_payment_methods: { ...options.previousPaymentMethods },
      expected_amount_remaining: options.amountRemaining,
      expected_invoice_ids: [...options.invoiceIds],
      currency,
      attempt_id: options.attemptId,
    },
    charged: 0,
    inProgressRetries: 0,
    authentication: null,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

const OUTCOMES: readonly tPayPendingDebtsContract2Outcome[] = [
  'paid',
  'partial',
  'declined',
  'requires_action',
  'debt_changed',
];

/** The contract-2 result, from the service's body (`{ success, data }`) or the result itself. */
function readResult(body: unknown): Record<string, unknown> | null {
  const candidate =
    isRecord(body) && isRecord(body.data) ? body.data : body;
  if (!isRecord(candidate)) return null;
  return OUTCOMES.some((outcome) => outcome === candidate.result)
    ? candidate
    : null;
}

/** `errors.status` (leadbilling's payment errors) or `errors.code` (its role gate). */
function readErrorCode(body: unknown): string | null {
  if (!isRecord(body) || !isRecord(body.errors)) return null;
  return stringOrNull(body.errors.status) ?? stringOrNull(body.errors.code);
}

const ROLE_REFUSALS: readonly string[] = ['role_required', 'owner_required'];

function finish(
  attempt: tPastDueAttempt,
  outcome: tPastDueAttemptOutcome,
): tPastDueStep {
  return { kind: 'finish', attempt, outcome };
}

function paymentDecline(options: {
  raw: unknown;
  attempt: tPastDueAttempt;
  currency: string | null;
  cardsRestored: unknown;
}): tPastDueDecline | null {
  const { raw, attempt } = options;
  if (!isRecord(raw)) return null;
  const cardsRestored =
    options.cardsRestored === 'restored' ||
    options.cardsRestored === 'not_restored'
      ? options.cardsRestored
      : 'not_applicable';
  return {
    group: toPastDueDeclineGroup(stringOrNull(raw.text_group)),
    retryAllowed: raw.retry_allowed === true,
    paymentMethodId:
      stringOrNull(raw.payment_method_id) ?? attempt.paymentMethodId,
    cardBrand: stringOrNull(raw.card_brand),
    cardLast4: stringOrNull(raw.card_last4),
    currency: options.currency ?? attempt.currency,
    declinedAt: parseSummaryDate(stringOrNull(raw.declined_at)),
    cardsRestored,
    origin: 'payment',
    retryLimit: toRetryLimit(stringOrNull(raw.retry_limit)),
  };
}

/** A refusal that leaves money already charged in this attempt is `partial`. */
function declineOrPartial(options: {
  attempt: tPastDueAttempt;
  decline: tPastDueDecline;
  remaining: number;
}): tPastDueStep {
  const { attempt, decline, remaining } = options;
  return attempt.charged > 0
    ? finish(attempt, {
        kind: 'partial',
        decline,
        charged: attempt.charged,
        remaining,
      })
    : finish(attempt, { kind: 'declined', decline });
}

export function resolvePastDuePostAnswer(options: {
  attempt: tPastDueAttempt;
  answer: tPastDuePostAnswer;
}): tPastDueStep {
  const { attempt, answer } = options;

  if (!answer.ok) {
    const code = readErrorCode(answer.body);
    if (code === 'payment_in_progress') {
      // Nothing was charged by this POST: the charge of the same attempt is
      // still running. The same POST, same attempt, gets its result.
      return attempt.inProgressRetries < PAST_DUE_IN_PROGRESS_MAX_RETRIES
        ? {
            kind: 'post',
            attempt: {
              ...attempt,
              inProgressRetries: attempt.inProgressRetries + 1,
            },
            delayMs: PAST_DUE_IN_PROGRESS_RETRY_MS,
          }
        : finish(attempt, { kind: 'ourError' });
    }
    if (code === 'attempt_reused') {
      return finish(attempt, { kind: 'attemptReused' });
    }
    if (code === 'paymentMethodNotFound') {
      return finish(attempt, { kind: 'paymentMethodNotFound' });
    }
    if (code !== null && ROLE_REFUSALS.includes(code)) {
      return finish(attempt, { kind: 'forbidden' });
    }
    // A network error, a timeout, a 5xx or the BFF's 424: the payment may
    // have gone through. No retry in this session (KTD-4).
    return finish(attempt, { kind: 'ourError' });
  }

  const data = readResult(answer.body);
  if (data === null) return finish(attempt, { kind: 'ourError' });

  const amountPaid = numberOrNull(data.amount_paid) ?? 0;
  const amountRemaining = numberOrNull(data.amount_remaining);
  const currency = stringOrNull(data.currency);
  const charged = attempt.charged + amountPaid;
  const settled: tPastDueAttempt = {
    ...attempt,
    charged,
    inProgressRetries: 0,
  };

  switch (data.result) {
    case 'paid':
      return finish(settled, { kind: 'paid', charged });
    case 'debt_changed':
      return finish(settled, { kind: 'debtChanged', charged });
    case 'requires_action': {
      const action = data.requires_action;
      const clientSecret = isRecord(action)
        ? stringOrNull(action.payment_intent_client_secret)
        : null;
      if (!isRecord(action) || clientSecret === null) {
        return finish(settled, { kind: 'ourError' });
      }
      const afterAmount = numberOrNull(action.amount_remaining_after_action);
      const afterIds = Array.isArray(action.invoice_ids_after_action)
        ? action.invoice_ids_after_action.filter(
            (id): id is string => typeof id === 'string',
          )
        : null;
      const remaining = amountRemaining ?? attempt.amountShown - charged;
      const next =
        afterAmount !== null && afterIds !== null
          ? { amountRemaining: afterAmount, invoiceIds: afterIds }
          : null;
      return {
        kind: 'authenticate',
        clientSecret,
        attempt: {
          ...settled,
          authentication: {
            invoiceAmount:
              next !== null ? Math.max(0, remaining - next.amountRemaining) : 0,
            remaining,
            next,
          },
        },
      };
    }
    case 'partial':
    case 'declined': {
      const decline = paymentDecline({
        raw: data.decline,
        attempt,
        currency,
        cardsRestored: data.cards_restored,
      });
      if (decline === null) return finish(settled, { kind: 'ourError' });
      return declineOrPartial({
        attempt: settled,
        decline,
        remaining: amountRemaining ?? attempt.amountShown - charged,
      });
    }
    default:
      return finish(settled, { kind: 'ourError' });
  }
}

export function resolvePastDueAuthentication(options: {
  attempt: tPastDueAttempt;
  /** `stripe.confirmPayment` answered without `error`. */
  authenticated: boolean;
  now: Date;
}): tPastDueStep {
  const { attempt, authenticated, now } = options;
  const authentication = attempt.authentication;
  if (authentication === null) return finish(attempt, { kind: 'ourError' });

  if (!authenticated) {
    // The customer cancelled or the bank did not finish it. Not a refusal,
    // never «3D Secure» on screen (M-21): the card can be retried.
    const decline: tPastDueDecline = {
      group: 'authentication_incomplete',
      retryAllowed: true,
      paymentMethodId: attempt.paymentMethodId,
      cardBrand: attempt.cardBrand,
      cardLast4: attempt.cardLast4,
      currency: attempt.currency,
      declinedAt: now,
      cardsRestored: 'not_applicable',
      origin: 'payment',
      retryLimit: null,
    };
    return declineOrPartial({
      attempt: { ...attempt, authentication: null },
      decline,
      remaining: authentication.remaining,
    });
  }

  // Without what remains after the authentication there is no honest
  // expected debt for the next POST: say we could not confirm, never guess.
  if (authentication.next === null) {
    return finish(
      {
        ...attempt,
        charged: attempt.charged + authentication.invoiceAmount,
        authentication: null,
      },
      { kind: 'ourError' },
    );
  }

  return {
    kind: 'post',
    delayMs: 0,
    attempt: {
      ...attempt,
      charged: attempt.charged + authentication.invoiceAmount,
      inProgressRetries: 0,
      authentication: null,
      request: {
        ...attempt.request,
        expected_amount_remaining: authentication.next.amountRemaining,
        expected_invoice_ids: [...authentication.next.invoiceIds],
      },
    },
  };
}

/** Our own error: no card is blamed, the button stays off until a reload. */
export function ourErrorDecline(): tPastDueDecline {
  return {
    group: 'our_error',
    retryAllowed: false,
    paymentMethodId: null,
    cardBrand: null,
    cardLast4: null,
    currency: null,
    declinedAt: null,
    cardsRestored: 'not_applicable',
    origin: 'payment',
    retryLimit: null,
  };
}

/** «Ya se cobraron $X de $Y»: what was charged, of what the screen showed, and what is left. */
export function pastDuePartialAmounts(options: {
  attempt: tPastDueAttempt;
  charged: number;
  remaining: number;
}): NonNullable<tPastDueLocalState['partial']> {
  const { attempt } = options;
  const format = (amountMinorUnits: number) =>
    formatDebtAmount({
      amountMinorUnits,
      currency: attempt.displayCurrency,
    }) ?? '';
  return {
    paid: format(options.charged),
    total: format(attempt.amountShown),
    remaining: format(options.remaining),
  };
}

/** What the zone shows once the attempt ends, and what the hook reads next. */
export function describePastDueOutcome(options: {
  outcome: tPastDueAttemptOutcome;
  attempt: tPastDueAttempt;
}): {
  local: Pick<tPastDueLocalState, 'decline' | 'partial'> & {
    phase: Extract<tPastDuePaymentPhase, 'idle' | 'confirming' | 'partial'>;
  };
  followUp: tPastDueFollowUp;
} {
  const { outcome, attempt } = options;

  switch (outcome.kind) {
    case 'paid':
      return {
        local: { phase: 'confirming', decline: null, partial: null },
        followUp: 'confirmPaid',
      };
    case 'partial':
      return {
        local: {
          phase: 'partial',
          decline: outcome.decline,
          partial: pastDuePartialAmounts({
            attempt,
            charged: outcome.charged,
            remaining: outcome.remaining,
          }),
        },
        followUp: 'rereadSummary',
      };
    case 'declined':
      return {
        local: { phase: 'idle', decline: outcome.decline, partial: null },
        followUp: 'rereadSummary',
      };
    case 'debtChanged':
      return {
        local: { phase: 'confirming', decline: null, partial: null },
        followUp: 'rereadDebt',
      };
    case 'paymentMethodNotFound':
      return {
        local: { phase: 'idle', decline: null, partial: null },
        followUp: 'rereadCards',
      };
    case 'attemptReused':
    case 'forbidden':
      return {
        local: { phase: 'idle', decline: null, partial: null },
        followUp: 'rereadSummary',
      };
    case 'ourError':
      // The charge may have gone through (a timeout, a 5xx, an authenticated
      // invoice with no after-action values): read the summary once so every
      // screen converges on what Stripe holds. The decline stays our error.
      return {
        local: { phase: 'idle', decline: ourErrorDecline(), partial: null },
        followUp: 'rereadSummary',
      };
  }
}
